<template>
  <div>
    <v2-modal
      :show="show"
      size="xl"
      @close="onCloseClick"
    >
      <!-- Header: title + custom step indicator -->
      <template #header>
        <div class="v2-ship-wizard__header">
          <div class="v2-ship-wizard__title-row">
            <h2 class="v2-ship-wizard__title">
              Ship All
            </h2>
          </div>
          <div class="v2-ship-wizard__steps">
            <div
              v-for="(label, idx) in stepLabels"
              :key="label"
              class="v2-ship-wizard__step"
              :class="{
                'v2-ship-wizard__step--active': currentStep === idx + 1,
                'v2-ship-wizard__step--completed': currentStep > idx + 1,
                'v2-ship-wizard__step--pending': currentStep < idx + 1,
              }"
            >
              {{ label }}
            </div>
          </div>
        </div>
      </template>

      <!-- Body -->
      <div
        v-if="isCheckingActive"
        class="v2-ship-wizard__loading"
      >
        <v2-spinner size="lg" />
        <p class="v2-ship-wizard__loading-text">
          Checking for active shipments…
        </p>
      </div>

      <v2-ship-all-collection-step
        v-if="!isCheckingActive && currentStep === 1"
        :selected-collection-id="selectedCollectionId"
        @select="onSelectCollection"
      />

      <v2-ship-all-product-step
        v-if="!isCheckingActive && currentStep === 2"
        :collection-id="selectedCollectionId"
        :collection-name="selectedCollection"
        :selected-product-ids="selectedProductIds"
        @update="onUpdateProducts"
      />

      <v2-ship-all-order-step
        v-if="!isCheckingActive && currentStep === 3"
        :collection-id="selectedCollectionId"
        :product-ids="selectedProductIds"
        :selected-order-ids="selectedOrderIds"
        @update="onUpdateOrders"
      />

      <v2-ship-all-processing-step
        v-if="!isCheckingActive && currentStep === 4"
        :batch-id="batchId"
        :batch-status="batchStatus"
        :triggered-by="triggeredBy"
        @status-update="onBatchStatusUpdate"
        @retry="onRetry"
      />

      <!-- Footer: Back / Next / Create Shipment / Done -->
      <template #footer>
        <template v-if="currentStep < 4">
          <v2-button
            variant="outline"
            :disabled="currentStep === 1"
            @click="prevStep"
          >
            Back
          </v2-button>
          <v2-button
            v-if="currentStep < 3"
            variant="primary"
            :disabled="!canProceed"
            @click="nextStep"
          >
            Next
          </v2-button>
          <v2-button
            v-if="currentStep === 3"
            variant="primary"
            :disabled="selectedOrderIds.length === 0 || isSubmitting"
            @click="onShipAll"
          >
            <v2-spinner
              v-if="isSubmitting"
              size="sm"
            />
            Create Shipment ({{ selectedOrderIds.length }})
          </v2-button>
        </template>
        <template v-else>
          <v2-button
            variant="primary"
            :disabled="batchStatus === 'processing'"
            @click="onClose"
          >
            Done
          </v2-button>
        </template>
      </template>
    </v2-modal>

    <!-- Confirm dialog used both for Ship All confirmation and Close-mid-flow guard. -->
    <v2-modal
      :show="confirm.show"
      size="sm"
      :title="confirm.title"
      @close="onConfirmCancel"
    >
      <p class="v2-ship-wizard__confirm-body">
        {{ confirm.message }}
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="onConfirmCancel"
        >
          {{ confirm.cancelLabel }}
        </v2-button>
        <v2-button
          :variant="confirm.okVariant"
          @click="onConfirmAccept"
        >
          {{ confirm.okLabel }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import store from '@/store'
import { CREATE_BULK_SHIPMENT, GET_ACTIVE_BULK_SHIPMENT } from '@/store/modules/shipment.module'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2ShipAllCollectionStep from './steps/V2ShipAllCollectionStep.vue'
import V2ShipAllProductStep from './steps/V2ShipAllProductStep.vue'
import V2ShipAllOrderStep from './steps/V2ShipAllOrderStep.vue'
import V2ShipAllProcessingStep from './steps/V2ShipAllProcessingStep.vue'

const EMPTY_CONFIRM = {
  show: false,
  title: '',
  message: '',
  okLabel: 'OK',
  cancelLabel: 'Cancel',
  okVariant: 'primary',
  onAccept: null,
  onCancel: null,
}

export default {
  name: 'V2ShipAllWizard',
  components: {
    V2Modal,
    V2Button,
    V2Spinner,
    V2ShipAllCollectionStep,
    V2ShipAllProductStep,
    V2ShipAllOrderStep,
    V2ShipAllProcessingStep,
  },
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      stepLabels: ['Collection', 'Products', 'Orders', 'Processing'],
      currentStep: 1,
      selectedCollection: '',
      selectedCollectionId: '',
      selectedProductIds: [],
      selectedOrderIds: [],
      batchId: null,
      batchStatus: null,
      triggeredBy: '',
      isSubmitting: false,
      isCheckingActive: false,
      /** Monotonic id so stale `GET_ACTIVE_BULK_SHIPMENT` completions cannot affect a newer open. */
      activeBulkCheckSession: 0,
      hasNavigationGuard: false,
      navigationGuard: null,
      confirm: { ...EMPTY_CONFIRM },
    }
  },
  computed: {
    canProceed() {
      if (this.currentStep === 1) return !!this.selectedCollectionId
      if (this.currentStep === 2) return this.selectedProductIds.length > 0
      return true
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.onShow()
        else this.onHidden()
      },
    },
  },
  // Belt-and-suspenders cleanup. The navigation guard is normally torn
  // down via onHidden when the `show` prop flips to false, but the
  // wizard is also mounted under `v-if` in V2OrderList. If the parent
  // unmounts the wizard while it's still open (e.g. isBrand flips, the
  // KP_2829_BULK_SHIPMENT flag changes, or the user logs out mid-flow),
  // the show watcher won't fire and the router.beforeEach guard would
  // leak on a destroyed instance, intercepting every subsequent navigation.
  beforeDestroy() {
    this.removeNavigationGuard()
  },
  methods: {
    async onShow() {
      this.activeBulkCheckSession += 1
      const session = this.activeBulkCheckSession
      this.resetState()
      this.addNavigationGuard()
      this.isCheckingActive = true
      try {
        const res = await store.dispatch(GET_ACTIVE_BULK_SHIPMENT)
        if (!this.show || this._isDestroyed) return
        if (session !== this.activeBulkCheckSession) return
        this.applyActiveBatchResponse(res)
      } catch {
        // If check fails, just start fresh — mirrors V1 behavior.
      } finally {
        if (session === this.activeBulkCheckSession) {
          this.clearCheckingFlagAfterActiveCheck()
        }
      }
    },
    // Race guard helper: the active-batch dispatch is async, so the
    // user may have closed (or unmounted) the wizard between dispatch
    // and response. `onShow` also gates on `activeBulkCheckSession` so a
    // prior request cannot apply after a rapid close/reopen (when `show`
    // is true again). Bail without writing batchId/status/currentStep
    // onto a hidden wizard — otherwise V2ShipAllProcessingStep would
    // mount and begin its 2s polling loop invisibly against a stale
    // batch. Extracted to keep onShow under CodeScene's complexity
    // threshold while preserving the guard semantics.
    applyActiveBatchResponse(res) {
      if (!this.show || this._isDestroyed) return
      const batch = res?.data?.data?.batch
      if (!batch) return
      this.batchId = batch.batchId
      this.batchStatus = batch.status
      this.triggeredBy = batch.triggeredBy || ''
      this.currentStep = 4
    },
    // End the active-batch check phase for the owning `onShow` session
    // only (`onShow` calls this from `finally` when the session still
    // matches). If the user closed while the request was in flight,
    // leaving isCheckingActive true would strand state until the next
    // open (and confuses any code that reads the flag while show is false).
    clearCheckingFlagAfterActiveCheck() {
      if (!this._isDestroyed) {
        this.isCheckingActive = false
      }
    },
    onHidden() {
      this.removeNavigationGuard()
      if (this._isDestroyed) return
      // Invalidate any in-flight active-batch check from the previous
      // open so its late resolution can't reanimate state on a closed
      // wizard.
      this.activeBulkCheckSession += 1
      // Reset step + batch fields so the processing step's
      // `v-if="!isCheckingActive && currentStep === 4"` becomes false
      // and the component unmounts. Without this, closing the modal
      // during processing leaves V2ShipAllProcessingStep mounted behind
      // the hidden V2Modal and its 2s GET_BULK_SHIPMENT_STATUS poll
      // keeps firing indefinitely. Unmount fires `beforeDestroy →
      // stopPolling()` in the step, clearing the setTimeout.
      // Reopening the wizard re-runs GET_ACTIVE_BULK_SHIPMENT in
      // onShow, which restores currentStep=4 + the batch fields if
      // the batch is still active.
      this.resetState()
      this.isCheckingActive = false
    },
    resetState() {
      this.currentStep = 1
      this.selectedCollection = ''
      this.selectedCollectionId = ''
      this.selectedProductIds = []
      this.selectedOrderIds = []
      this.batchId = null
      this.batchStatus = null
      this.triggeredBy = ''
      this.isSubmitting = false
    },
    nextStep() {
      if (this.currentStep < 4) this.currentStep += 1
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1
    },
    onSelectCollection({ id, name }) {
      if (this.selectedCollectionId !== id) {
        this.selectedCollectionId = id
        this.selectedCollection = name
        this.selectedProductIds = []
        this.selectedOrderIds = []
      }
    },
    onUpdateProducts(productIds) {
      this.selectedProductIds = productIds
      // Reset orders when products change (V1 parity)
      this.selectedOrderIds = []
    },
    onUpdateOrders(orderIds) {
      this.selectedOrderIds = orderIds
    },
    onShipAll() {
      this.openConfirm({
        title: 'Confirm Bulk Shipment',
        message: `You are about to create shipments for ${this.selectedOrderIds.length} orders. This action cannot be undone. Do you want to continue?`,
        okLabel: 'Yes, Ship All',
        okVariant: 'primary',
        cancelLabel: 'Cancel',
        onAccept: () => this.executeShipAll(),
      })
    },
    async executeShipAll() {
      this.isSubmitting = true
      try {
        const res = await store.dispatch(CREATE_BULK_SHIPMENT, {
          collectionId: this.selectedCollectionId,
          productIds: this.selectedProductIds,
          orderIds: this.selectedOrderIds,
        })
        if (!this.show || this._isDestroyed) return
        this.batchId = res.data.data.batchId
        this.batchStatus = 'processing'
        this.currentStep = 4
      } catch (err) {
        apiToastError(err)
      } finally {
        this.isSubmitting = false
      }
    },
    onBatchStatusUpdate(status) {
      this.batchStatus = status.status
      if (status.triggeredBy) this.triggeredBy = status.triggeredBy
      if (status.status === 'completed') {
        const {
          succeeded, failed, skipped, totalOrders,
        } = status
        const parts = [`${succeeded} of ${totalOrders} shipments created`]
        if (failed > 0) parts.push(`${failed} failed`)
        if (skipped > 0) parts.push(`${skipped} skipped`)
        apiToastSuccess(parts.join(', '))
      }
    },
    onRetry() {
      this.batchStatus = 'processing'
    },
    onCloseClick() {
      // Guard against accidental close mid-flow (steps 2-3 have user input).
      if (this.currentStep > 1 && this.currentStep < 4) {
        this.openConfirm({
          title: 'Close Wizard?',
          message: 'Are you sure you want to close? Your selections will be lost.',
          okLabel: 'Yes, close',
          okVariant: 'destructive',
          cancelLabel: 'Cancel',
          onAccept: () => this.onClose(),
        })
      } else {
        this.onClose()
      }
    },
    onClose() {
      this.removeNavigationGuard()
      this.$emit('closed')
    },
    // Single source of truth for releasing any pending confirm dialog.
    // Calls the dialog's onCancel (if any) before wiping state, so
    // callers that hold a router `next` callback inside onCancel get
    // a clean `next(false)` instead of being orphaned. Detaches the
    // confirm state BEFORE invoking the cancel callback so the cancel
    // can't re-enter and trigger an infinite loop.
    drainPendingConfirm() {
      if (!this.confirm.show) return
      const prevCancel = this.confirm.onCancel
      this.confirm = { ...EMPTY_CONFIRM }
      if (typeof prevCancel === 'function') prevCancel()
    },
    openConfirm(opts) {
      // Release any prior confirm before overwriting — without this the
      // router navigation guard's `next(false)` (stashed inside the prior
      // dialog's onCancel) would be silently dropped, hanging Vue Router.
      this.drainPendingConfirm()
      this.confirm = { ...EMPTY_CONFIRM, show: true, ...opts }
    },
    onConfirmCancel() {
      const cb = this.confirm.onCancel
      this.confirm = { ...EMPTY_CONFIRM }
      if (typeof cb === 'function') cb()
    },
    onConfirmAccept() {
      const cb = this.confirm.onAccept
      this.confirm = { ...EMPTY_CONFIRM }
      if (typeof cb === 'function') cb()
    },
    addNavigationGuard() {
      if (this.hasNavigationGuard) return
      this.navigationGuard = this.$router.beforeEach((to, from, next) => {
        if (this.currentStep > 1 && this.currentStep < 4) {
          this.openConfirm({
            title: 'Leave page?',
            message: 'Are you sure you want to leave? Your Ship All progress will be lost.',
            okLabel: 'Yes, leave',
            okVariant: 'destructive',
            cancelLabel: 'Stay',
            onAccept: () => {
              this.onClose()
              next()
            },
            onCancel: () => next(false),
          })
        } else {
          this.onClose()
          next()
        }
      })
      this.hasNavigationGuard = true
    },
    removeNavigationGuard() {
      // Drain any open route-leave confirm dialog first. Its onCancel
      // holds Vue Router's `next(false)` callback — if we tear down
      // the guard without firing onCancel, the in-flight navigation
      // never resolves and the router stays stuck thinking a nav is
      // pending. drainPendingConfirm is a no-op when no dialog is open.
      this.drainPendingConfirm()
      if (this.navigationGuard) {
        this.navigationGuard()
        this.navigationGuard = null
        this.hasNavigationGuard = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-ship-wizard {
  &__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__steps {
    display: flex;
    align-items: stretch;
    gap: var(--v2-space-2);
    width: 100%;
  }

  &__step {
    flex: 1;
    text-align: center;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--v2-muted-foreground);
    border-bottom: 2px solid var(--v2-glass-stroke);
    transition: color 0.15s ease, border-color 0.15s ease;

    &--active {
      color: var(--v2-primary);
      border-bottom-color: var(--v2-primary);
    }

    &--completed {
      color: oklch(0.62 0.18 145);
      border-bottom-color: oklch(0.62 0.18 145);
    }

    &--pending {
      color: var(--v2-muted-foreground);
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-12) 0;
  }

  &__loading-text {
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
    margin: 0;
  }

  &__confirm-body {
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin: 0;
    padding: var(--v2-space-1) 0 var(--v2-space-2);
  }
}
</style>
